import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff, Building2 } from 'lucide-react';

const BOLogin = () => {
  const navigate = useNavigate();
  const { login } = useBOAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const response = await boAPI.login({ email: email.trim(), password });
      login(response.data.token, response.data.user);
      toast.success(`Welcome back, ${response.data.user.full_name}!`);
      navigate('/backoffice');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-900 mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-neutral-900" data-testid="bo-login-title">Medici Homes</h1>
          <p className="text-neutral-500 mt-1">Back Office Management</p>
        </div>

        <Card className="border-neutral-border shadow-card">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-serif text-xl">Sign In</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@medicihomes.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 rounded-xl"
                  data-testid="bo-login-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-xl pr-10"
                    data-testid="bo-login-password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full rounded-xl bg-primary-900 hover:bg-primary-800 text-white h-11"
                disabled={loading}
                data-testid="bo-login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BOLogin;
